import { useCallback, useMemo, useState } from "react";
import styles from "./LiveRegion.module.scss";

export interface Announcement {
    message: string;
    /** Changes on every announcement, repeated messages included. */
    id: number;
}

export interface Announcer {
    announcement: Announcement;
    announce: (message: string) => void;
}

/**
 * Screen-reader announcements for a `LiveRegion`. Each one gets a new id so
 * the region renders it as a new node: screen readers skip a repeated
 * message whose text doesn't change.
 */
export const useAnnouncer = (): Announcer => {
    const [announcement, setAnnouncement] = useState({ message: "", id: 0 });
    const announce = useCallback(
        (message: string) =>
            setAnnouncement(({ id }) => ({ message, id: id + 1 })),
        [],
    );
    return useMemo(
        () => ({ announcement, announce }),
        [announcement, announce],
    );
};

/**
 * A visually hidden region that speaks `useAnnouncer`'s announcements.
 * `polite` waits for the screen reader to finish; `assertive` interrupts it,
 * for feedback on a gesture in progress.
 */
export const LiveRegion = ({
    announcement,
    politeness = "polite",
}: Readonly<{
    announcement: Announcement;
    politeness?: "polite" | "assertive";
}>) => (
    <div
        role={politeness === "polite" ? "status" : undefined}
        aria-live={politeness}
        aria-atomic
        className={styles.liveRegion}
    >
        <span key={announcement.id}>{announcement.message}</span>
    </div>
);
